import React, { useState, useRef, useEffect } from 'react';
import { uploadLogo } from '../api/logos.api';

const LogoUploader = ({ onLogoExport, onClose, onLogoSaved, customerId }) => {
  const [image, setImage] = useState(null);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [error, setError] = useState('');
  const [fileSize, setFileSize] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 1024 * 1024; // 1MB in bytes
  const CANVAS_SIZE = 400;

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError(`El archivo es demasiado grande. Máximo: 1MB, Tu archivo: ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor carga un archivo de imagen válido');
      return;
    }

    setError('');
    setFileSize(file.size);

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setScale(1);
        setPosition({ x: 0, y: 0 });
      };
      img.onerror = () => {
        setError('No se pudo cargar la imagen');
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleMouseDown = (e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    if (!image) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((prev) => Math.max(0.1, Math.min(5, prev * delta)));
  };

  useEffect(() => {
    if (!image || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;

    // Clear canvas
    ctx.fillStyle = '#f3f4f6';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw grid
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      ctx.beginPath();
      ctx.moveTo((canvas.width / 4) * i, 0);
      ctx.lineTo((canvas.width / 4) * i, canvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, (canvas.height / 4) * i);
      ctx.lineTo(canvas.width, (canvas.height / 4) * i);
      ctx.stroke();
    }

    // Draw image
    const scaledWidth = image.width * scale;
    const scaledHeight = image.height * scale;
    ctx.drawImage(image, position.x, position.y, scaledWidth, scaledHeight);

    // Draw border
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }, [image, scale, position]);

  const handleExport = async () => {
    if (!canvasRef.current) return;

    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    const canvas = canvasRef.current;
    canvas.toBlob(async (blob) => {
      try {
        const file = new File([blob], 'logo-400x400.png', { type: 'image/png' });
        await uploadLogo(customerId, file);
        setSuccessMessage('Logo guardado exitosamente');
        if (onLogoSaved) {
          onLogoSaved();
        }
        setTimeout(() => {
          if (onLogoExport) {
            onLogoExport(blob);
          }
          onClose();
          window.location.reload();
        }, 1000);
      } catch (err) {
        setError(err.message || 'Error al guardar el logo');
        setIsLoading(false);
      }
    }, 'image/png');
  };

  const handleReset = () => {
    setImage(null);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setError('');
    setFileSize(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Cargar y Ajustar Logo</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 font-bold"
          >
            ✕
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* File Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecciona una imagen
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                cursor-pointer"
            />
            <p className="mt-1 text-xs text-gray-500">
              Máximo 1MB. Formatos: JPG, PNG, GIF, WebP
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <p className="text-sm text-green-800">{successMessage}</p>
            </div>
          )}

          {/* Canvas Preview */}
          {image && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vista Previa (400x400)
                </label>
                <div
                  onMouseDown={handleMouseDown}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                  onWheel={handleWheel}
                  className="cursor-grab active:cursor-grabbing select-none border-2 border-gray-300 rounded-lg overflow-auto bg-gray-50"
                  style={{ height: '450px' }}
                >
                  <canvas
                    ref={canvasRef}
                    className="m-auto block"
                    style={{ marginTop: '25px' }}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-600">
                  Arrastra para mover • Rueda del ratón para zoom
                </p>
              </div>

              {/* Controls */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zoom: {(scale * 100).toFixed(0)}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    step="10"
                    value={scale * 100}
                    onChange={(e) => setScale(e.target.value / 100)}
                    className="w-full"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                  <div>
                    <span className="font-medium">Posición X:</span>
                    <p className="text-gray-600">{position.x.toFixed(0)}px</p>
                  </div>
                  <div>
                    <span className="font-medium">Posición Y:</span>
                    <p className="text-gray-600">{position.y.toFixed(0)}px</p>
                  </div>
                  <div>
                    <span className="font-medium">Tamaño archivo:</span>
                    <p className="text-gray-600">{(fileSize / 1024).toFixed(2)}KB</p>
                  </div>
                  <div>
                    <span className="font-medium">Dimensiones original:</span>
                    <p className="text-gray-600">{image.width}x{image.height}px</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleReset}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Limpiar
                </button>
                <button
                  onClick={handleExport}
                  disabled={isLoading}
                  className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-md font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      Guardando...
                    </>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LogoUploader;
